import React, { useMemo } from 'react';
import { useCurrencySymbol } from '../../../../../utils/currencyUtils';
import {
    parseAmountNumber,
    formatAmountComma,
} from '../../../../../utils/decimalAmountUtils';
import { useSelector } from 'react-redux';
import {
  canonicalInstallationAssociatedTaskDescription,
  isTrainingExpenseDisplayRow,
  sumInstallationAssociatedTrainingValues,
} from "../../../../../utils/trainingOtherExpenseSync";

const ActualSoftwareTable = ({ matchedProjects, filteredQuotation, currentPerson, tasks, AssociatedRef, selectedProject }) => {

    const softwarePersons = currentPerson;
    const currency = useCurrencySymbol();
    const trainingRowLabel = useMemo(
        () => canonicalInstallationAssociatedTaskDescription("Training cost", currency),
        [currency]
    );
    const formatMoneyComma = formatAmountComma;
    const { mytrainings } = useSelector((state) => state.myTrainings);

    const getMyTrainingAmountForAssignee = useMemo(() => {
        const trainingsList = Array.isArray(mytrainings?.trainings)
            ? mytrainings.trainings
            : Array.isArray(mytrainings)
                ? mytrainings
                : [];

        const resolveRefId = (ref) => {
            if (ref == null || ref === "") return "";
            if (typeof ref === "object") return String(ref?._id ?? ref?.id ?? "").trim();
            return String(ref).trim();
        };

        return (project, assigneeId) => {
            if (!project || !assigneeId) return 0;
            const assignee = resolveRefId(assigneeId);
            if (!assignee) return 0;

            const projectId = String(project?._id || project?.ProjectID || "").trim().toLowerCase();
            const quotationId = String(project?.quotationId || project?.quotationID || project?.quotationid || "").trim();
            const projectName = String(project?.ProjectName || project?.projectname || "").trim().toLowerCase();
            const leadId = String(project?.leadid || project?.leadId || project?.LeadId || project?.LeadID || "").trim();

            const matched = trainingsList.filter((t) => {
                const assignedTo = resolveRefId(t?.assignedto ?? t?.assignedTo);
                if (!assignedTo || assignedTo !== assignee) return false;

                const tProjectId = resolveRefId(t?.projectid ?? t?.projectId ?? t?.project).toLowerCase();
                const tQuotationId = String(t?.quotationId ?? t?.quotationID ?? t?.quotationid ?? "").trim();
                const tProjectName = String(t?.projectname || "").trim().toLowerCase();
                const tLeadId = String(t?.leadid ?? t?.leadId ?? "").trim();

                return (
                    (projectId && tProjectId && projectId === tProjectId) ||
                    (quotationId && tQuotationId && quotationId === tQuotationId) ||
                    (projectName && tProjectName && projectName === tProjectName) ||
                    (leadId && tLeadId && leadId === tLeadId)
                );
            });

            return matched.reduce((sum, t) => {
                const amount = Number(
                    String(t?.trainingcost ?? t?.trainingCost ?? 0)
                        .toString()
                        .replace(/,/g, "")
                );
                return sum + (Number.isFinite(amount) ? amount : 0);
            }, 0);
        };
    }, [mytrainings]);

    const getTaskOwnerId = (task) =>
        task?.taskownerid && typeof task.taskownerid === "object" && task.taskownerid._id != null
            ? task.taskownerid._id
            : task?.taskownerid;

    const taskAmount = (task) => parseAmountNumber(task?.expenses ?? task?.value);

    // Group persons by manager and their operations
    const groupPersonsByManager = () => {
        const managers = softwarePersons.filter(p => p.roles?.[0] === "Project manager");
        const operations = softwarePersons.filter(p => p.roles?.[0] === "Operations");

        const grouped = managers.map(manager => {
            // Find operations persons that belong to this manager (same ProjectID)
            const managerOperations = operations.filter(op => op.ProjectID === manager.ProjectID);

            return {
                manager,
                operations: managerOperations
            };
        });

        return grouped;
    };

    // Get person display name with manager-specific numbering
    // Get person display name with manager-specific numbering
    const getPersonDisplayName = (person) => {
        const serviceType = person.typeService || "Software";
        const role = person.roles?.[0] || "";

        if (role === "Project manager") {
            // Find manager number
            const managers = softwarePersons.filter(p => p.roles?.[0] === "Project manager");
            const managerNumber = managers.findIndex(p => p.personId === person.personId) + 1;
            return `${serviceType} Manager ${managerNumber}`;
        } else if (role === "Operations") {
            // Find which manager this operations person belongs to and their number within that manager
            const grouped = groupPersonsByManager();

            for (const group of grouped) {
                const operationIndex = group.operations.findIndex(op => op.personId === person.personId);
                if (operationIndex !== -1) {
                    // Find manager number for this group
                    // const managers = softwarePersons.filter(p => p.roles?.[0] === "Project manager");
                    // const managerNumber = managers.findIndex(p => p.personId === group.manager.personId) + 1;
                    return `${serviceType} Employee ${operationIndex + 1}`;
                }
            }

            // Fallback if no manager found
            return `${serviceType} Employee`;
        } else {
            return `${serviceType} ${role}`;
        }
    };

    const personMatchesTaskType = (person, task) => {
        const pType = String(person?.typeService || "").trim().toLowerCase();
        const tType = String(task?.typeservice || "").trim().toLowerCase();
        if (!pType) return true;
        if (!tType) return true;
        return tType === pType;
    };

    const projectCtxForTraining = (person) => ({
        _id: person.ProjectID,
        ProjectID: person.ProjectID,
        ProjectName: selectedProject?.ProjectName,
        quotationId: selectedProject?.quotationId,
        leadid: selectedProject?.leadId ?? selectedProject?.leadid,
    });

    const getMyTrainingSliceForPerson = (person) => {
        // For Actual P&L, Associated Cost should include trainings for BOTH manager and operations person.
        return getMyTrainingAmountForAssignee(
            projectCtxForTraining(person),
            person.personId
        );
    };

    const getTaskTrainingTotalForPerson = (person) => {
        if (!tasks?.length) return 0;
        const pid = String(person.ProjectID || "").trim();
        const oid = String(person.personId || "").trim();
        const pType = String(person?.typeService || "").trim().toLowerCase();
        return sumInstallationAssociatedTrainingValues(
            tasks.filter((task) => {
                if (String(task?.projectid || "").trim() !== pid) return false;
                if (String(getTaskOwnerId(task) || "").trim() !== oid) return false;
                if (!isTrainingExpenseDisplayRow(task)) return false;
                if (!pType) return true;
                const tType = String(task?.typeservice || "").trim().toLowerCase();
                if (!tType) return true;
                return tType === pType;
            })
        );
    };

    const getTrainingTotalForPerson = (person) =>
        getTaskTrainingTotalForPerson(person) + getMyTrainingSliceForPerson(person);

    // Get ALL tasks for ALL persons (regardless of project)
    const getAllPersonsTasks = () => {
        return softwarePersons.flatMap(person =>
            tasks.filter(task =>
                String(getTaskOwnerId(task) || "").trim() === String(person.personId || "").trim() &&
                String(task.projectid || "").trim() === String(person.ProjectID || "").trim() &&
                personMatchesTaskType(person, task) &&
                !isTrainingExpenseDisplayRow(task)
            )
        );
    };

    const allPersonsTasks = getAllPersonsTasks();
    const allDescriptions = [...new Set(allPersonsTasks.map(task => task.description).filter(Boolean))];

    const hasAnyTraining = softwarePersons.some((p) => getTrainingTotalForPerson(p) > 0);
    const displayDescriptions = hasAnyTraining
        ? [trainingRowLabel, ...allDescriptions.filter((d) => d !== trainingRowLabel)]
        : allDescriptions;

    // Helper function to get total for a specific description and person
    const getDescriptionTotalForPerson = (description, personId) => {
        if (description === trainingRowLabel) {
            const person = softwarePersons.find((p) => String(p.personId) === String(personId));
            return person ? getTrainingTotalForPerson(person) : 0;
        }
        const matchingTasks = allPersonsTasks.filter(
            task => task.description === description && String(getTaskOwnerId(task) || "").trim() === String(personId || "").trim()
        );
        return matchingTasks.reduce((sum, task) => sum + taskAmount(task), 0);
    };

    // Calculate total cost for each person
    const getPersonTotalCost = (personId) => {
        const person = softwarePersons.find((p) => String(p.personId) === String(personId));
        if (!person) return 0;
        const fromTasks = allPersonsTasks
            .filter(task => String(getTaskOwnerId(task) || "").trim() === String(personId || "").trim())
            .reduce((sum, task) => sum + taskAmount(task), 0);
        return fromTasks + getTrainingTotalForPerson(person);
    };

    return (
        displayDescriptions.length > 0 ? (
            <div ref={AssociatedRef} className='pt-5 pl-2'>
                <div className="text-lg font-semibold pb-2">
                    <h2>Associated Costs: {currentPerson[0]?.typeService}</h2>
                </div>
                {/* Vertical Table for Multiple Users */}
                <div className="inline-block">
                    {/* Header Row */}
                    <div className="flex font-semibold text-xs border-x">
                        <div className="w-40 bg-[#e8e8e8] p-3 flex border-b justify-start align-middle items-center">Task</div>
                        {softwarePersons.map((person, index) => (
                            <div key={index} className="w-40 bg-[#e8e8e8] border-b capitalize p-3  text-center flex justify-center align-middle items-center">
                                {getPersonDisplayName(person)}<br />
                                {/* <div className="text-xs opacity-80">({person.personName})</div> */}
                            </div>
                        ))}
                    </div>
                    {/* Totals Row */}
                    <div className="flex text-xs border-x">
                        <div className="w-40 p-3 flex justify-start align-middle border-b font-semibold items-center">Total ({currency})</div>
                        {softwarePersons.map((person, index) => (
                            <div key={index} className="w-40 p-3 text-center border-b font-semibold flex justify-center align-middle items-center">
                                {formatMoneyComma(getPersonTotalCost(person.personId))}
                            </div>
                        ))}
                    </div>
                    {/* Scrollable Descriptions */}
                    <div className="max-h-60 narrow-scrollbar overflow-auto inline-block border-x">
                        {displayDescriptions.map((description, index) => (
                            <div
                                key={index}
                                className={`flex text-xs `}
                            >
                                <div className="w-40 p-3 flex  border-b justify-start align-middle items-center">
                                    {description}
                                </div>
                                {softwarePersons.map((person, personIndex) => {
                                    const descriptionTotal = getDescriptionTotalForPerson(description, person.personId);
                                    return (
                                        <div key={personIndex} className="w-40 p-3 border-b text-center flex justify-center align-middle items-center">
                                            {descriptionTotal > 0 ? formatMoneyComma(descriptionTotal) : '-'}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        ) : (
            <>
                <div ref={AssociatedRef} className='pt-5 pl-2'>
                    <div className="text-lg font-semibold pb-2">
                        <h2>Associated Costs: {currentPerson[0]?.typeService}</h2>
                    </div>
                    <div className="w-full bg-white flex h-10 border-b">
                        <div className="w-full py-3 flex items-center justify-start text-start text-red-500 text-sm font-semibold">
                            No data available
                        </div>
                    </div>
                </div>
            </>
        )
    )
}

export default ActualSoftwareTable
